import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useModalDialog } from '../hooks/useModalDialog';
import './CookieConsent.css';

const AUTO_DISMISS_SECONDS = 30;

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });
  const [showDetails, setShowDetails] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_DISMISS_SECONDS);
  const bannerRef = useRef(null);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    // Checking existing cookie consent

    if (!consent) {
      setIsVisible(true);
      // No consent found, showing banner
    } else {
      // Consent already given, banner hidden
    }
  }, []);

  const handleAcceptAll = () => {
    // User accepted all cookies
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
    
    // Enable analytics if accepted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted'
      });
      // Analytics consent granted
    }
  };

  const handleRejectAll = useCallback(() => {
    // User rejected non-essential cookies (also used for auto-dismiss timeout)
    const consentData = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };

    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);

    // Deny analytics if rejected
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied'
      });
      // Analytics consent denied
    }
  }, []);

  // useModalDialog only wires up Escape-to-close when given an onClose —
  // without one (as before), Escape did nothing here. Reject All (rather
  // than Accept All) is the safe default for "the user dismissed this
  // without an explicit choice", matching the auto-dismiss timeout below.
  //
  // Pass handleRejectAll directly rather than wrapping it in a fresh
  // arrow function — useModalDialog's effect depends on `onClose`, and an
  // inline `() => handleRejectAll()` is a new reference every render. This
  // component re-renders every second (the countdown timer below), so
  // that would tear down and rebuild the whole modal-dialog setup
  // (re-focusing the first element, re-registering the keydown listener)
  // once a second — handleRejectAll is already a stable useCallback, so
  // passing it as-is keeps the effect from re-running unnecessarily.
  useModalDialog(bannerRef, { isOpen: isVisible, onClose: handleRejectAll });

  // Auto-dismiss after a period of inactivity so switch-scanning AAC users
  // aren't blocked if they can't reach the banner in time. Only necessary
  // cookies are kept (equivalent to Reject All) since consent for
  // analytics/marketing must come from an explicit user action, not a
  // timeout. Any keyboard or pointer activity while the banner is open
  // resets the countdown, per WCAG 2.2.1 (Timing Adjustable).
  useEffect(() => {
    if (!isVisible) return undefined;

    setSecondsLeft(AUTO_DISMISS_SECONDS);

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const resetTimer = () => setSecondsLeft(AUTO_DISMISS_SECONDS);
    const banner = bannerRef.current;
    banner?.addEventListener('keydown', resetTimer);
    banner?.addEventListener('pointerdown', resetTimer);

    return () => {
      clearInterval(interval);
      banner?.removeEventListener('keydown', resetTimer);
      banner?.removeEventListener('pointerdown', resetTimer);
    };
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && secondsLeft === 0) {
      handleRejectAll();
    }
  }, [isVisible, secondsLeft, handleRejectAll]);

  const handleSavePreferences = () => {
    // User saved custom preferences
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
    
    // Update analytics consent based on preferences
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied',
        'ad_storage': preferences.marketing ? 'granted' : 'denied'
      });
      // Analytics consent updated based on preferences
    }
  };

  const handlePreferenceChange = (type, value) => {
    // Preference changed
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`cookie-consent-overlay ${isVisible ? 'show' : ''}`}>
      <div ref={bannerRef} tabIndex={-1} className={`cookie-consent-banner ${isVisible ? 'show' : 'hide'}`} role="dialog" aria-labelledby="cookie-title" aria-describedby="cookie-description">
        <div className="cookie-content">
          <h2 id="cookie-title" className="cookie-title">
            🍪 Cookie Settings
          </h2>
          
          <p id="cookie-description" className="cookie-description">
            We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts.
            You can customize your preferences below or accept all cookies.
          </p>

          <p className="cookie-timer" aria-hidden="true">
            Auto-closing in {secondsLeft}s (only necessary cookies) — press any key to keep this open
          </p>
          <p className="sr-only" aria-live="polite">
            {secondsLeft <= 10 ? `Cookie banner auto-closing in ${secondsLeft} second${secondsLeft === 1 ? '' : 's'}.` : ''}
          </p>

          {!showDetails ? (
            <div className="cookie-actions">
              <button 
                onClick={handleAcceptAll}
                className="btn btn-primary cookie-btn"
                aria-label="Accept all cookies"
              >
                Accept All
              </button>
              
              <button 
                onClick={handleRejectAll}
                className="btn btn-outline cookie-btn"
                aria-label="Reject non-essential cookies"
              >
                Reject All
              </button>
              
              <button 
                onClick={() => setShowDetails(true)}
                className="btn btn-secondary cookie-btn"
                aria-label="Customize cookie preferences"
              >
                Customize
              </button>
            </div>
          ) : (
            <div className="cookie-preferences">
              <div className="preference-group">
                <div className="preference-item">
                  <label className="preference-label">
                    <input 
                      type="checkbox" 
                      checked={preferences.necessary}
                      disabled
                      aria-describedby="necessary-desc"
                    />
                    <span className="preference-name">Necessary Cookies</span>
                    <span className="required-badge">Required</span>
                  </label>
                  <p id="necessary-desc" className="preference-description">
                    Essential for the website to function properly. These cannot be disabled.
                  </p>
                </div>

                <div className="preference-item">
                  <label className="preference-label">
                    <input 
                      type="checkbox" 
                      checked={preferences.analytics}
                      onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                      aria-describedby="analytics-desc"
                    />
                    <span className="preference-name">Analytics Cookies</span>
                  </label>
                  <p id="analytics-desc" className="preference-description">
                    Help us understand how visitors interact with our website by collecting anonymous information.
                  </p>
                </div>

                <div className="preference-item">
                  <label className="preference-label">
                    <input 
                      type="checkbox" 
                      checked={preferences.marketing}
                      onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                      aria-describedby="marketing-desc"
                    />
                    <span className="preference-name">Marketing Cookies</span>
                  </label>
                  <p id="marketing-desc" className="preference-description">
                    Used to track visitors across websites to display relevant advertisements.
                  </p>
                </div>
              </div>

              <div className="cookie-actions">
                <button 
                  onClick={handleSavePreferences}
                  className="btn btn-primary cookie-btn"
                  aria-label="Save cookie preferences"
                >
                  Save Preferences
                </button>
                
                <button 
                  onClick={() => setShowDetails(false)}
                  className="btn btn-outline cookie-btn"
                  aria-label="Go back to simple options"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          <div className="cookie-links">
            <a href="/privacy-policy" className="cookie-link">
              Privacy & Cookie Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;